import { forwardRef, useState } from 'react';
import type { ChangeEvent } from 'react';

import {
  useMonkeysBaseAppearance,
  useMonkeysComponentAttributes,
} from '../../provider/context';
import type { BaseSwitchProps } from './types';
import { resolveBaseAppearance } from './theme';
import { cn, hasNonEmptyRenderableNode, hasRenderableNode } from './utils';

const trackSizeClassNames = {
  sm: 'h-5 w-9',
  md: 'h-6 w-10',
  lg: 'h-7 w-12',
} as const;

const thumbSizeClassNames = {
  sm: 'h-3.5 w-3.5',
  md: 'h-4 w-4',
  lg: 'h-5 w-5',
} as const;

const checkedThumbClassNames = {
  sm: 'translate-x-[18px]',
  md: 'translate-x-5',
  lg: 'translate-x-6',
} as const;

const densitySize = {
  compact: 'sm',
  default: 'md',
  comfortable: 'lg',
} as const;

export const BaseSwitch = forwardRef<HTMLInputElement, BaseSwitchProps>(
  function BaseSwitch(
    {
      label,
      description,
      disabled = false,
      controlSize,
      appearance,
      className,
      classNames,
      style,
      checked,
      defaultChecked,
      onChange,
      ...switchProps
    },
    ref,
  ) {
    if (typeof disabled !== 'boolean') {
      throw new TypeError('BaseSwitch disabled must be a boolean.');
    }
    if (
      controlSize !== undefined &&
      controlSize !== 'sm' &&
      controlSize !== 'md' &&
      controlSize !== 'lg'
    ) {
      throw new TypeError(
        'BaseSwitch controlSize must be "sm", "md", or "lg".',
      );
    }

    const resolvedAppearance = useMonkeysBaseAppearance(appearance);
    const componentAttributes = useMonkeysComponentAttributes();
    const theme = resolveBaseAppearance(resolvedAppearance);
    const hasLabel = hasRenderableNode(label);
    const hasDescription = hasNonEmptyRenderableNode(description);
    const [uncontrolledChecked, setUncontrolledChecked] = useState(
      defaultChecked ?? false,
    );
    const isControlled = checked !== undefined;
    const resolvedChecked = isControlled ? checked : uncontrolledChecked;
    const state = resolvedChecked ? 'checked' : 'unchecked';
    const resolvedVisualSize = controlSize ?? densitySize[theme.density];

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
      if (disabled) return;
      if (!isControlled) {
        setUncontrolledChecked(event.currentTarget.checked);
      }
      onChange?.(event);
    };

    return (
      <label
        {...componentAttributes}
        data-monkeys-component="switch"
        data-slot="root"
        data-state={state}
        data-size={controlSize}
        data-disabled={disabled || undefined}
        className={cn(
          theme.slots.switchRoot,
          !hasDescription && 'items-center',
          disabled && theme.slots.switchDisabled,
          classNames?.root,
          className,
        )}
        style={{
          ...theme.styles.switchRoot,
          ...style,
        }}
      >
        <span
          data-slot="track"
          data-state={state}
          data-size={controlSize}
          className={cn(
            theme.slots.switchTrack,
            !hasDescription && 'mt-0',
            resolvedChecked && theme.slots.switchChecked,
            controlSize && trackSizeClassNames[controlSize],
            classNames?.track,
          )}
          style={theme.styles.switchTrack}
        >
          <input
            {...switchProps}
            ref={ref}
            type="checkbox"
            role="switch"
            disabled={disabled}
            checked={checked}
            defaultChecked={defaultChecked}
            aria-checked={resolvedChecked}
            data-slot="input"
            data-state={state}
            data-size={controlSize}
            data-disabled={disabled || undefined}
            onChange={handleChange}
            className={cn(theme.slots.switchInput, classNames?.input)}
          />
          <span
            aria-hidden="true"
            data-slot="thumb"
            data-state={state}
            data-size={controlSize}
            className={cn(
              theme.slots.switchThumb,
              controlSize && thumbSizeClassNames[controlSize],
              resolvedChecked && checkedThumbClassNames[resolvedVisualSize],
              classNames?.thumb,
            )}
            style={theme.styles.switchThumb}
          />
        </span>

        {hasLabel || hasDescription ? (
          <span
            data-slot="content"
            className={cn(theme.slots.switchContent, classNames?.content)}
          >
            {hasLabel ? (
              <span
                data-slot="label"
                className={cn(theme.slots.switchLabel, classNames?.label)}
              >
                {label}
              </span>
            ) : null}
            {hasDescription ? (
              <span
                data-slot="description"
                className={cn(
                  theme.slots.switchDescription,
                  classNames?.description,
                )}
              >
                {description}
              </span>
            ) : null}
          </span>
        ) : null}
      </label>
    );
  },
);
